<template>
  <div :class="{
    'pin-input': true,
    'pin-input-error': error,
    'pin-input-loading': loading,
  }">
    <input
      v-for="i in length"
      :key="i"
      :data-pin-input="i"
      :type="type"
      :ref="`input-${i - 1}`"
      :value="valueAt(i - 1)"
      :placeholder="placeholderAt(i - 1)"
      enterkeyhint="next"
      @paste.prevent="handlePasteEvent($event)"
      @keyup="handleKeyEvent(i - 1, $event)"
    />
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'PinInput',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    // any kind of initial value (will be treated just like a pasted value)
    value: String,
    // a placeholder (will be treated similar to the pasted value but as placeholder)
    // if it is shorter as `length` indicates, the first char is used for all inputs
    placeholder: {
      type: String,
      default: '',
    },
    // the fixed length of the PIN
    length: {
      type: Number,
      validate: (v) => v >= 1,
      default: () => 6,
    },
    // HTML type of the input
    // useful for pure numbers as it results in a number keyboard for mobile devices
    type: {
      type: String,
      validate: (v) => ['number', 'text'].includes(v),
      default: 'text',
    },
    // transform any entered key, e.g., from "a" to "A"
    transform: {
      type: String,
      validate: (v) => ['uppercase', 'lowercase', ''].includes(v),
      default: '',
    },
    // toggle error highlights on (true) or off (false)
    error: {
      type: Boolean,
      default: false,
    },
    // toggle loading highlights on (true) or off (false)
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    fullValue: (vm) => vm.input.join(''),
    valueAt: (vm) => (index) => vm.input?.[index] || undefined,
    placeholderAt: (vm) => (index) => {
      const p = vm.placeholder;

      if (!p || !p.length) {
        return '';
      }

      if (p.length >= 1 && p.length < vm.length) {
        return p[0];
      }

      return p[index];
    },
  },
  watch: {
    value(newValue) {
      if (newValue !== this.fullValue) {
        this.insertFullValue(newValue);
      }
    },
  },
  data: () => ({
    input: [],
  }),
  methods: {
    handleKeyEvent(index, event) {
      const key = event.code || event.key;

      // paste needs to be handled on the dedicated `@paste` event to get access to the data
      if (event.ctrlKey && key === 'KeyV') {
        return;
      }

      switch (key) {
        // arrow functions trigger a navigation between inputs
        case 'ArrowLeft':
        case 'ArrowDown':
          this.move(index, -1);
          event.preventDefault();
          return;
        case 'ArrowRight':
        case 'ArrowUp':
          this.move(index, 1);
          event.preventDefault();
          return;
        // try to trigger the events (see .emit for details)
        case 'Enter':
          this.emit();
          event.preventDefault();
          return;
        // remove at index
        case 'Backspace':
        case 'Delete':
          // notice: if you test mobiles with Browserstack, your keypress won't be
          // propagated correctly; please always use the native keyboard of the device
          this.remove(index);
          event.preventDefault();
          return;
        // don't do anything for just special keys
        case 'Tab':
        case 'ShiftLeft':
        case 'ShiftRight':
        case 'ControlLeft':
        case 'ControlRight':
          return;
        default:
          break;
      }

      // fast typers will result in a value more than just one character
      const { value } = event.target;
      if (value.length > 0) {
        for (let i = 0; i < Math.min(value.length, this.length - index); i += 1) {
          this.update(index + i, value[i]);
        }
      }
    },
    handlePasteEvent(event) {
      // notice: if you test mobile devices with Browserstack the paste might be
      // emulated by entering the values char by char which is not the same as
      // the original paste on the platform
      const value = (event.clipboardData || window.clipboardData).getData('text');
      this.insertFullValue(value);
    },
    /**
     * insert full text to the individual inputs
     * - value will be trimmed
     * - value will be cut if too long
     * - value will be transformed exactly like manual inputs
     */
    insertFullValue(value) {
      const workValue = value.trim();
      for (let i = 0; i < this.length; i += 1) {
        const charAt = workValue[i];
        if (typeof charAt === 'string') {
          this.update(i, charAt);
        }
      }
    },
    /**
     * update the input at index with value
     * - ignore empty (or whitespace) inputs
     * - also perform some transformations, if configured via the property
     */
    update(index, value) {
      if (!value) {
        return;
      }

      if (value.trim().length === 0) {
        Vue.set(this.input, index, '');
        return;
      }

      const transformations = {
        uppercase: (v) => v.toUpperCase(),
        lowercase: (v) => v.toLowerCase(),
      };
      let valueToSet = value;
      if (this.transform && transformations[this.transform]) {
        valueToSet = transformations[this.transform](value);
      }

      Vue.set(this.input, index, valueToSet);
      Vue.nextTick(() => {
        this.move(index, 1);
        this.emit();
      });
    },
    /**
     * remove char at index and move left
     * special case: if the current input is already empty, try to delete the last one
     */
    remove(index) {
      let targetIndex = index;
      const valueAtIndex = this.input?.[targetIndex]?.trim() || '';
      if (valueAtIndex === '' && targetIndex - 1 >= 0) {
        targetIndex -= 1;
      }

      Vue.set(this.input, targetIndex, '');
      Vue.nextTick(() => {
        this.move(targetIndex + 1, -1);
        this.emit();
      });
    },
    /**
     * move the focus in a certain direction (+1 or -1) based on the index
     */
    move(index, direction) {
      let next = index + direction;

      // cannot move further than to the first or last field
      if (next < 0) {
        next = 0;
      } else if (next > this.length - 1) {
        next = this.length - 1;
      }

      const ref = this.$refs[`input-${next}`];
      if (ref && ref[0]) {
        ref[0].focus();
        ref[0].select();
      }
    },
    /**
     * emits the possible events
     * - `change` on any change
     * - `submit` if the PIN length is reached
     */
    emit() {
      const v = this.fullValue;
      this.$emit('change', v);
      if (v.length === this.length) {
        this.$emit('submit', v);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pin-input {
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 380px) {
    gap: 7.5px;
  }

  input {
    width: 1em;
    height: 1em;
    padding: .75em .5em;
    font-family: var(--font-regular-family);
    font-size: 1.5em;
    font-weight: bold;
    text-align: center;
    color: var(--colors-main-font);
    border: 0;
    border-bottom: 2px solid var(--colors-main-font);
    background-color: transparent;
    outline: none;
    transition: border-color ease-out var(--times-transition-fast);
    -webkit-appearance: none;

    @media (max-width: 380px) {
      padding: .6em .3em;
      font-size: 1.2em;
    }

    &::placeholder {
      color: var(--colors-main-font);
      opacity: .6;
    }

    &:focus,
    &:focus-visible {
      border-color: var(--colors-primary);
    }
  }

  &.pin-input-error input {
    border-color: var(--colors-error-font);
  }

  &.pin-input-loading input {
    border-color: var(--colors-primary);
  }
}
</style>
